/**
 * React Query hooks for the Components screen. The catalog refetches every
 * 60s (manifests change rarely); every mutation invalidates both the catalog
 * and the Infrastructure components query so session counts stay in step.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ComponentCreate,
  ComponentEdit,
  createComponent,
  deleteComponent,
  fetchComponents,
  updateComponent
} from '@/api/components';

const KEYS = [['components'], ['adminInfraComponents']];

function useInvalidate() {
  const qc = useQueryClient();
  return () => KEYS.forEach(k => qc.invalidateQueries({ queryKey: k }));
}

export function useComponents() {
  return useQuery({
    queryKey: ['components'],
    queryFn: fetchComponents,
    refetchInterval: 60_000
  });
}

export function useUpdateComponent() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ComponentEdit }) =>
      updateComponent(id, body),
    onSuccess: invalidate
  });
}

export function useCreateComponent() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ComponentCreate) => createComponent(body),
    onSuccess: invalidate
  });
}

export function useDeleteComponent() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => deleteComponent(id),
    onSuccess: invalidate
  });
}
